<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">Tax Configuration</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">
                <v-icon class="mr-1">mdi-calendar-multiple</v-icon>
                Tax year list
              </div>
              <p class="text-caption text-grey mt-1">
                Select year, then choose version to edit configuration
              </p>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end ga-2">
              <v-btn
                color="primary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-import"
                rounded="lg"
                @click="openImportDialog"
              >
                IMPORT
                <v-tooltip activator="parent" location="top">Import tax config bundle from JSON file</v-tooltip>
              </v-btn>
              <v-btn
                data-testid="add-year-btn"
                color="primary"
                class="control-btn create-btn"
                variant="flat"
                size="large"
                prepend-icon="mdi-plus-circle"
                rounded="lg"
                @click="openAddYearDialog"
              >
                ADD YEAR
                <v-tooltip activator="parent" location="top">Add new tax year</v-tooltip>
              </v-btn>
              <v-btn
                data-testid="settings-btn"
                color="primary"
                class="control-btn"
                variant="outlined"
                size="large"
                prepend-icon="mdi-cog"
                rounded="lg"
                @click="openSettingsDialog"
              >
                SETTINGS
                <v-tooltip activator="parent" location="top">Configure Tax Planning prompts &amp; AI model</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-data-table
          data-testid="tax-year-table"
          :headers="headers"
          :items="yearRows"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          hide-default-body
        >
          <template v-slot:colgroup>
            <col
              v-for="header in headers"
              :key="header.key"
              :style="{ width: header.width, minWidth: header.width }"
            />
          </template>

          <template v-slot:tbody>
            <tbody v-if="yearRows.length" class="v-data-table__tbody">
              <template v-for="(row, index) in yearRows" :key="row.taxYear">
                <tr class="cfg-year-parent-row">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>
                    <button class="cfg-tree-toggle" type="button" @click="toggleExpand(row.taxYear)">
                      <v-icon
                        :class="['cfg-tree-chevron', { 'is-open': isExpanded(row.taxYear) }]"
                        size="18"
                      >
                        mdi-chevron-right
                      </v-icon>
                      <span class="cfg-tree-title-block">
                        <span class="cfg-tree-title">{{ row.taxYear }}</span>
                        <span class="cfg-tree-subtitle">{{ row.year }}</span>
                      </span>
                    </button>
                  </td>
                  <td>{{ row.description || '-' }}</td>
                  <td>{{ row.effectiveStartDate || '-' }}</td>
                  <td>{{ row.effectiveEndDate || '-' }}</td>
                  <td>{{ row.updatedDate }}</td>
                  <td class="text-center">
                    <v-switch
                      :model-value="row.isActive"
                      :aria-label="`Set tax year ${row.taxYear} active`"
                      color="success"
                      density="compact"
                      hide-details
                      inset
                      @update:model-value="onToggleYearActive(row.taxYear, $event)"
                    />
                  </td>
                  <td class="text-center">
                    <div class="action-group">
                      <v-btn
                        :data-testid="`add-version-${row.taxYear}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="primary"
                        class="action-btn"
                        @click="openAddVersionDialog(row)"
                      >
                        <v-icon size="18">mdi-source-branch-plus</v-icon>
                        <v-tooltip activator="parent" location="top">Add version</v-tooltip>
                      </v-btn>
                      <v-btn
                        :data-testid="`import-version-${row.taxYear}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="info"
                        class="action-btn"
                        @click="openImportVersionDialog(row)"
                      >
                        <v-icon size="18">mdi-import</v-icon>
                        <v-tooltip activator="parent" location="top">Import bundle as new version</v-tooltip>
                      </v-btn>
                      <v-btn
                        :data-testid="`edit-year-${row.taxYear}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="warning"
                        class="action-btn"
                        @click="openEditYearDialog(row)"
                      >
                        <v-icon size="18">mdi-pencil-outline</v-icon>
                        <v-tooltip activator="parent" location="top">Edit year</v-tooltip>
                      </v-btn>
                      <v-btn
                        :data-testid="`delete-year-${row.taxYear}`"
                        variant="tonal"
                        density="comfortable"
                        icon
                        color="error"
                        class="action-btn"
                        @click="openDeleteYearDialog(row)"
                      >
                        <v-icon size="18">mdi-delete-outline</v-icon>
                        <v-tooltip activator="parent" location="top">Delete year</v-tooltip>
                      </v-btn>
                    </div>
                  </td>
                </tr>
                <template v-if="isExpanded(row.taxYear)">
                  <template
                    v-for="(versionRow, vIdx) in versionsOf(row.taxYear)"
                    :key="`${versionRow.taxYear}-${versionRow.versionCode}`"
                  >
                    <tr class="cfg-version-child-row">
                      <td />
                      <td class="cfg-version-child-cell">
                        <span class="cfg-version-branch" />
                        <button
                          class="cfg-tree-toggle"
                          type="button"
                          :aria-expanded="isVersionExpanded(versionRow.taxYear, versionRow.versionCode)"
                          @click="toggleVersionExpand(versionRow.taxYear, versionRow.versionCode)"
                        >
                          <v-icon
                            :class="['cfg-tree-chevron', { 'is-open': isVersionExpanded(versionRow.taxYear, versionRow.versionCode) }]"
                            size="16"
                          >
                            mdi-chevron-right
                          </v-icon>
                          <span class="cfg-tree-title-block">
                            <span class="cfg-tree-title">{{ versionRow.versionCode }}</span>
                            <span class="cfg-tree-subtitle">
                              Version {{ vIdx + 1 }} of {{ versionsOf(row.taxYear).length }}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td>{{ versionRow.description || '-' }}</td>
                      <td>{{ versionRow.effectiveStartDate || '-' }}</td>
                      <td>{{ versionRow.effectiveEndDate || '-' }}</td>
                      <td>{{ versionRow.updatedDate }}</td>
                      <td class="text-center">
                        <v-switch
                          :model-value="versionRow.isActive"
                          :aria-label="`Set version ${versionRow.versionCode} active`"
                          color="success"
                          density="compact"
                          hide-details
                          inset
                          @update:model-value="onToggleVersionActive(versionRow.taxYear, versionRow.versionCode, $event)"
                        />
                      </td>
                      <td class="text-center">
                        <div class="action-group">
                          <v-btn
                            variant="tonal"
                            density="comfortable"
                            icon
                            color="info"
                            class="action-btn"
                            @click="onExportJson(versionRow)"
                          >
                            <v-icon size="18">mdi-download-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Export JSON</v-tooltip>
                          </v-btn>
                          <v-btn
                            :data-testid="`edit-version-${versionRow.taxYear}-${versionRow.versionCode}`"
                            variant="tonal"
                            density="comfortable"
                            icon
                            color="warning"
                            class="action-btn"
                            @click="openEditor(versionRow)"
                          >
                            <v-icon size="18">mdi-pencil-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Edit configuration</v-tooltip>
                          </v-btn>
                          <v-btn
                            :data-testid="`delete-version-${versionRow.taxYear}-${versionRow.versionCode}`"
                            variant="tonal"
                            density="comfortable"
                            icon
                            color="error"
                            class="action-btn"
                            @click="openDeleteVersionDialog(versionRow)"
                          >
                            <v-icon size="18">mdi-delete-outline</v-icon>
                            <v-tooltip activator="parent" location="top">Delete version</v-tooltip>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <template v-if="isVersionExpanded(versionRow.taxYear, versionRow.versionCode)">
                      <!-- Loading state -->
                      <tr
                        v-if="isSubLoading(versionRow.taxYear, versionRow.versionCode)"
                        :key="`${versionRow.taxYear}-${versionRow.versionCode}-loading`"
                        class="cfg-subtree-row"
                      >
                        <td />
                        <td colspan="7" class="cfg-subtree-status">
                          <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2" />
                          กำลังโหลดเวอร์ชัน...
                        </td>
                      </tr>

                      <!-- Error state (endpoints need the DB applied) -->
                      <tr
                        v-else-if="subError(versionRow.taxYear, versionRow.versionCode)"
                        :key="`${versionRow.taxYear}-${versionRow.versionCode}-error`"
                        class="cfg-subtree-row"
                      >
                        <td />
                        <td colspan="7" class="cfg-subtree-status is-error">
                          <v-icon size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
                          {{ subError(versionRow.taxYear, versionRow.versionCode) }}
                        </td>
                      </tr>

                      <!-- Loaded: two clearly-separated groups (Question then Calculate) -->
                      <template v-else>
                        <!-- ── Tax Question group ─────────────────────────── -->
                        <tr
                          :key="`${versionRow.taxYear}-${versionRow.versionCode}-qhead`"
                          :data-testid="`cfg-subtree-${versionRow.taxYear}-${versionRow.versionCode}`"
                          class="cfg-subgroup-row cfg-subgroup-question"
                        >
                          <td />
                          <td colspan="7" class="cfg-subgroup-cell">
                            <span class="cfg-subgroup-branch" />
                            <span class="cfg-subgroup-header" data-testid="cfg-group-question">
                              <v-icon size="16" color="primary" class="mr-1">mdi-comment-question-outline</v-icon>
                              <span class="cfg-subgroup-title">Tax Question</span>
                              <span class="cfg-subgroup-count">
                                ({{ questionVersionsFor(versionRow.taxYear, versionRow.versionCode).length }})
                              </span>
                            </span>
                            <span class="cfg-subgroup-actions">
                              <v-btn
                                size="small"
                                variant="tonal"
                                color="primary"
                                prepend-icon="mdi-plus"
                                :data-testid="`cfg-q-add-${configVersionIdOf(versionRow.taxYear, versionRow.versionCode)}`"
                                :disabled="configVersionIdOf(versionRow.taxYear, versionRow.versionCode) == null"
                                @click="addQuestion(versionRow.taxYear, versionRow.versionCode)"
                              >
                                เพิ่ม
                              </v-btn>
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-for="qv in questionVersionsFor(versionRow.taxYear, versionRow.versionCode)"
                          :key="`q-${qv.questionVersionID}`"
                          :data-testid="`cfg-q-row-${qv.questionVersionID}`"
                          class="cfg-subversion-row cfg-subversion-question"
                        >
                          <td />
                          <td class="cfg-subversion-cell">
                            <span class="cfg-subversion-branch" />
                            <span class="cfg-subversion-code">{{ qv.versionCode }}</span>
                          </td>
                          <td>{{ qv.description || '-' }}</td>
                          <td />
                          <td />
                          <td />
                          <td class="text-center">
                            <v-switch
                              :model-value="qv.isActive"
                              :data-testid="`cfg-q-active-${qv.questionVersionID}`"
                              :aria-label="`Set question version ${qv.versionCode} active`"
                              color="success"
                              density="compact"
                              hide-details
                              inset
                              @update:model-value="toggleQuestionActive(versionRow.taxYear, versionRow.versionCode, qv, $event)"
                            />
                          </td>
                          <td class="text-center">
                            <div class="action-group">
                              <v-btn
                                :data-testid="`cfg-q-edit-${qv.questionVersionID}`"
                                variant="tonal"
                                density="comfortable"
                                size="small"
                                color="warning"
                                prepend-icon="mdi-pencil-outline"
                                @click="openQuestionEditor(versionRow.taxYear, versionRow.versionCode, qv.questionVersionID)"
                              >
                                Edit
                              </v-btn>
                              <v-btn
                                :data-testid="`cfg-q-del-${qv.questionVersionID}`"
                                variant="tonal"
                                density="comfortable"
                                icon
                                size="small"
                                color="error"
                                class="action-btn"
                                :aria-label="`Delete question version ${qv.versionCode}`"
                                @click="deleteQuestionVersion(versionRow.taxYear, versionRow.versionCode, qv)"
                              >
                                <v-icon size="18">mdi-delete-outline</v-icon>
                                <v-tooltip activator="parent" location="top">Delete version</v-tooltip>
                              </v-btn>
                            </div>
                          </td>
                        </tr>
                        <tr
                          v-if="!questionVersionsFor(versionRow.taxYear, versionRow.versionCode).length"
                          :key="`${versionRow.taxYear}-${versionRow.versionCode}-qempty`"
                          class="cfg-subversion-row cfg-subversion-empty"
                        >
                          <td />
                          <td colspan="7" class="cfg-subversion-empty-cell">
                            — ยังไม่มี Tax Question version —
                          </td>
                        </tr>

                        <!-- ── Tax Calculate group ────────────────────────── -->
                        <tr
                          :key="`${versionRow.taxYear}-${versionRow.versionCode}-chead`"
                          class="cfg-subgroup-row cfg-subgroup-calculate"
                        >
                          <td />
                          <td colspan="7" class="cfg-subgroup-cell">
                            <span class="cfg-subgroup-branch" />
                            <span class="cfg-subgroup-header" data-testid="cfg-group-calculate">
                              <v-icon size="16" color="info" class="mr-1">mdi-calculator-variant-outline</v-icon>
                              <span class="cfg-subgroup-title">Tax Calculate</span>
                              <span class="cfg-subgroup-count">
                                ({{ calcVersionsFor(versionRow.taxYear, versionRow.versionCode).length }})
                              </span>
                            </span>
                            <span class="cfg-subgroup-actions">
                              <v-btn
                                size="small"
                                variant="tonal"
                                color="info"
                                prepend-icon="mdi-plus"
                                :data-testid="`cfg-c-add-${configVersionIdOf(versionRow.taxYear, versionRow.versionCode)}`"
                                :disabled="configVersionIdOf(versionRow.taxYear, versionRow.versionCode) == null"
                                @click="addCalculate(versionRow.taxYear, versionRow.versionCode)"
                              >
                                เพิ่ม
                              </v-btn>
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-for="cv in calcVersionsFor(versionRow.taxYear, versionRow.versionCode)"
                          :key="`c-${cv.calcVersionID}`"
                          :data-testid="`cfg-c-row-${cv.calcVersionID}`"
                          class="cfg-subversion-row cfg-subversion-calculate"
                        >
                          <td />
                          <td class="cfg-subversion-cell">
                            <span class="cfg-subversion-branch" />
                            <span class="cfg-subversion-code">{{ cv.versionCode }}</span>
                          </td>
                          <td>{{ cv.description || '-' }}</td>
                          <td />
                          <td />
                          <td />
                          <td class="text-center">
                            <v-switch
                              :model-value="cv.isActive"
                              :data-testid="`cfg-c-active-${cv.calcVersionID}`"
                              :aria-label="`Set calc version ${cv.versionCode} active`"
                              color="success"
                              density="compact"
                              hide-details
                              inset
                              @update:model-value="toggleCalcActive(versionRow.taxYear, versionRow.versionCode, cv, $event)"
                            />
                          </td>
                          <td class="text-center">
                            <div class="action-group">
                              <v-btn
                                :data-testid="`cfg-c-edit-${cv.calcVersionID}`"
                                variant="tonal"
                                density="comfortable"
                                size="small"
                                color="warning"
                                prepend-icon="mdi-pencil-outline"
                                @click="openCalcEditor(versionRow.taxYear, versionRow.versionCode, cv.calcVersionID)"
                              >
                                Edit
                              </v-btn>
                              <v-btn
                                :data-testid="`cfg-c-del-${cv.calcVersionID}`"
                                variant="tonal"
                                density="comfortable"
                                icon
                                size="small"
                                color="error"
                                class="action-btn"
                                :aria-label="`Delete calc version ${cv.versionCode}`"
                                @click="deleteCalcVersion(versionRow.taxYear, versionRow.versionCode, cv)"
                              >
                                <v-icon size="18">mdi-delete-outline</v-icon>
                                <v-tooltip activator="parent" location="top">Delete version</v-tooltip>
                              </v-btn>
                            </div>
                          </td>
                        </tr>
                        <tr
                          v-if="!calcVersionsFor(versionRow.taxYear, versionRow.versionCode).length"
                          :key="`${versionRow.taxYear}-${versionRow.versionCode}-cempty`"
                          class="cfg-subversion-row cfg-subversion-empty"
                        >
                          <td />
                          <td colspan="7" class="cfg-subversion-empty-cell">
                            — ยังไม่มี Tax Calculate version —
                          </td>
                        </tr>
                      </template>
                    </template>
                  </template>
                  <tr
                    v-if="versionsOf(row.taxYear).length === 0"
                    :key="`${row.taxYear}-empty`"
                    class="cfg-version-child-row"
                  >
                    <td />
                    <td colspan="7" class="text-grey text-caption">
                      No versions yet — click "Add version" to create one
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
            <tbody v-else class="v-data-table__tbody">
              <tr>
                <td :colspan="headers.length">
                  <div class="no-data-container">
                    <div class="no-data-content">
                      <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                        mdi-calendar-blank-outline
                      </v-icon>
                      <div class="no-data-text">
                        <h3 class="no-data-title">No Tax Years Found</h3>
                        <p class="no-data-subtitle">Create your first tax year to get started.</p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <TaxYearDialog
      v-model="yearDialog"
      :year-options="yearOptions"
      @submit="createYear"
    />

    <TaxYearDialog
      v-model="editYearDialog"
      :year-options="yearOptions"
      :initial-data="editYearTarget"
      @submit="updateYear"
    />

    <v-dialog v-model="versionDialog" max-width="720px" persistent data-testid="version-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-source-branch-plus</v-icon>
          Add version{{ selectedYearForVersion ? ` for ${selectedYearForVersion.taxYear}` : '' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="versionForm" v-model="versionFormValid">
            <v-text-field
              data-testid="version-code-input"
              v-model="versionForm.versionCode"
              label="Version code *"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="V2"
              :rules="[rules.required]"
              @update:model-value="versionForm.versionCode = String(versionForm.versionCode || '').toUpperCase()"
            />

            <v-text-field
              data-testid="version-description-input"
              v-model="versionForm.description"
              label="Description"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="Notes about this version"
            />

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="version-startDate-input"
                  v-model="versionForm.effectiveStartDate"
                  label="Effective start date"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar-start"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="version-endDate-input"
                  v-model="versionForm.effectiveEndDate"
                  label="Effective end date"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar-end"
                />
              </v-col>
            </v-row>

            <v-switch
              data-testid="version-active-switch"
              v-model="versionForm.isActive"
              label="Active"
              color="success"
              inset
              hide-details
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="version-cancel-btn" variant="outlined" @click="closeVersionDialog">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="version-create-btn"
            color="primary"
            variant="elevated"
            :disabled="!versionFormValid"
            @click="createVersion"
          >
            <v-icon start>mdi-content-save</v-icon>
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" persistent data-testid="delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            This action cannot be undone.
          </v-alert>
          <p>{{ deleteMessage }}</p>
          <p class="font-weight-bold text-primary">
            {{ deleteTargetLabel }}
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="delete-cancel-btn" variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="delete-confirm-btn"
            color="error"
            variant="elevated"
            @click="confirmDelete"
          >
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="subDeleteDialog" max-width="500px" persistent data-testid="sub-delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            {{ subDeleteTarget && subDeleteTarget.kind === 'question'
              ? 'ลบชุดคำถามนี้พร้อมรายการทั้งหมด — ไม่สามารถย้อนกลับได้'
              : 'ลบชุดสูตรนี้พร้อม formulas / brackets / constants ทั้งหมด — ไม่สามารถย้อนกลับได้' }}
          </v-alert>
          <p class="font-weight-bold text-primary">
            {{ subDeleteTarget && subDeleteTarget.row
              ? `${subDeleteTarget.row.versionCode} - ${subDeleteTarget.row.description || ''}`
              : '' }}
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="sub-delete-cancel-btn" variant="outlined" @click="subDeleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn data-testid="sub-delete-confirm-btn" color="error" variant="elevated" @click="confirmSubDelete">
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importDialog" max-width="720px" persistent data-testid="import-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-import</v-icon>
          Import tax config bundle (new year)
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-2 text-medium-emphasis mb-3">
            The JSON file carries the full config bundle (rules, calc &amp; question versions).
            The fields below only set where it lands — the target tax year.
          </p>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                data-testid="import-year-select"
                v-model="importYearForm.beYear"
                :items="yearOptions"
                label="ปี พ.ศ. *"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar"
                @update:model-value="onImportYearChange"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                data-testid="import-taxYear-input"
                v-model="importYearForm.taxYear"
                label="Tax year code *"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-identifier"
                placeholder="TY2027"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="importYearForm.description"
                label="Description"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-text-box-outline"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="importYearForm.effectiveStartDate"
                label="Effective start date"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-start"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="importYearForm.effectiveEndDate"
                label="Effective end date"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-end"
              />
            </v-col>
          </v-row>
          <v-file-input
            label="Tax config bundle JSON file *"
            accept=".json"
            variant="outlined"
            density="comfortable"
            prepend-icon=""
            prepend-inner-icon="mdi-file-code-outline"
            class="mb-2"
            @update:model-value="onImportFileChange"
          />
          <v-alert
            v-if="importValidationMessage"
            :type="importJsonValid ? 'success' : 'error'"
            variant="tonal"
            density="compact"
            class="mb-2"
          >
            {{ importValidationMessage }}
          </v-alert>
          <v-btn
            v-if="importFile"
            variant="outlined"
            color="primary"
            size="small"
            class="mb-2"
            :loading="importValidating"
            @click="onValidateImportJson"
          >
            Validate JSON
          </v-btn>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="closeImportDialog">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :disabled="!importJsonValid"
            @click="onImportConfig"
          >
            <v-icon start>mdi-import</v-icon>
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importVersionDialog" max-width="720px" persistent data-testid="import-version-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-import</v-icon>
          Import bundle as new version{{ importVersionTargetYear ? ` for ${importVersionTargetYear.taxYear}` : '' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-2 text-medium-emphasis mb-3">
            The bundle is grafted as a NEW config version under this year. The version code comes
            from the file and is auto-incremented if it already exists here (never overwrites).
          </p>
          <v-text-field
            data-testid="import-version-code-input"
            v-model="importVersionForm.versionCode"
            label="Next version code (suggested)"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-tag-outline"
            placeholder="V2"
            class="mb-3"
            readonly
            persistent-hint
            hint="Informational only — the final code comes from the imported bundle and auto-increments on conflict."
          />
          <v-text-field
            v-model="importVersionForm.description"
            label="Description"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-text-box-outline"
            class="mb-3"
          />
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="importVersionForm.effectiveStartDate"
                label="Effective start date"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-start"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="importVersionForm.effectiveEndDate"
                label="Effective end date"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-end"
              />
            </v-col>
          </v-row>
          <v-file-input
            label="Tax config bundle JSON file *"
            accept=".json"
            variant="outlined"
            density="comfortable"
            prepend-icon=""
            prepend-inner-icon="mdi-file-code-outline"
            class="mb-2"
            @update:model-value="onImportVersionFileChange"
          />
          <v-alert
            v-if="importVersionValidationMessage"
            :type="importVersionJsonValid ? 'success' : 'error'"
            variant="tonal"
            density="compact"
            class="mb-2"
          >
            {{ importVersionValidationMessage }}
          </v-alert>
          <v-btn
            v-if="importVersionFile"
            variant="outlined"
            color="primary"
            size="small"
            class="mb-2"
            :loading="importVersionValidating"
            @click="onValidateImportVersionJson"
          >
            Validate JSON
          </v-btn>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="closeImportVersionDialog">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :disabled="!importVersionJsonValid"
            @click="onImportVersionConfig"
          >
            <v-icon start>mdi-import</v-icon>
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="settingsDialog" max-width="820px" persistent data-testid="settings-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-cog</v-icon>
          Tax Planning settings
        </v-card-title>
        <v-card-text v-if="settingsLoading" class="pt-4">
          <v-skeleton-loader
            type="heading, list-item-two-line, list-item-two-line, divider, heading, list-item, list-item, divider, heading, list-item-three-line"
          />
        </v-card-text>
        <v-card-text v-else class="pt-4">
          <!-- Prompts (per-state: source message + AI refine prompt) -->
          <div class="d-flex align-center justify-space-between mb-3">
            <h4 class="section-title mb-0">ข้อความ / Prompt ปรุงคำ (ต่อ state)</h4>
            <v-switch
              data-testid="settings-enable-refine"
              v-model="settingsForm.enablePromptRefine"
              label="ปรุงคำด้วย AI"
              color="primary"
              density="compact"
              hide-details
              inset
            />
          </div>
          <v-expansion-panels multiple variant="accordion" class="mb-2">
            <v-expansion-panel
              v-for="s in promptStates"
              :key="s.key"
              :data-testid="'settings-panel-' + s.key"
            >
              <v-expansion-panel-title>
                <v-icon size="small" class="mr-2">{{ s.icon }}</v-icon>
                {{ s.label }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-textarea
                  :data-testid="s.messageTestId"
                  v-model="settingsForm[s.messageField]"
                  label="ข้อความต้นฉบับ (message)"
                  :hint="s.hint"
                  :persistent-hint="!!s.hint"
                  variant="outlined"
                  auto-grow
                  rows="3"
                  density="comfortable"
                  class="mb-3"
                />
                <v-textarea
                  :data-testid="'settings-' + s.key + '-refine'"
                  v-model="settingsForm[s.refineField]"
                  label="Prompt ปรุงคำ (AI refine prompt)"
                  hint="ระบบจะนำ 'ข้อความต้นฉบับ' ด้านบนไปปรุงด้วย prompt นี้ผ่าน AI ก่อนส่งให้ผู้ใช้ (ต้องเปิด 'ปรุงคำด้วย AI' + ตั้งค่า AI Model ด้านล่าง)"
                  persistent-hint
                  variant="outlined"
                  auto-grow
                  rows="6"
                  density="comfortable"
                  :disabled="!settingsForm.enablePromptRefine"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-divider class="my-4" />

          <!-- Model selection -->
          <h4 class="section-title">AI Model</h4>
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                data-testid="settings-provider"
                v-model="settingsForm.provider"
                :items="listDDProvider"
                item-title="providerName"
                item-value="providerName"
                label="Provider"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-cloud-outline"
                @update:model-value="onSettingsProviderChange"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                data-testid="settings-model"
                v-model="settingsForm.model"
                :items="settingsModelOptions"
                item-title="modelDisplay"
                item-value="modelName"
                label="Model"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-brain"
                :disabled="!settingsForm.provider"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                data-testid="settings-gender"
                v-model="settingsForm.gender"
                :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                item-title="name"
                item-value="id"
                label="Gender"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-account"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- AI Parameters -->
          <h4 class="section-title">AI Parameters</h4>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                data-testid="settings-temperature"
                v-model.number="settingsForm.temperature"
                type="number"
                label="Temperature"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="settingsForm.topP"
                type="number"
                label="Top P"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="settingsForm.maxTokens"
                type="number"
                label="Max Tokens"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="settingsForm.frequencyPenalty"
                type="number"
                label="Frequency Penalty"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="settingsForm.presencePenalty"
                type="number"
                label="Presence Penalty"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="settingsForm.stopSequence"
                label="Stop Sequence"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Replace switches -->
          <h4 class="section-title">Replace options</h4>
          <v-switch
            data-testid="settings-input-replace"
            v-model="settingsForm.enableInputMessageReplace"
            label="InputMessage replacing"
            color="primary"
            density="comfortable"
            hide-details
            inset
          />
          <v-switch
            data-testid="settings-voice-replace"
            v-model="settingsForm.enableVoicePromptReplace"
            label="VoicePrompt replacing"
            color="primary"
            density="comfortable"
            hide-details
            inset
          />
          <v-switch
            data-testid="settings-language-replace"
            v-model="settingsForm.enableLanguageReplace"
            label="Language replacing"
            color="primary"
            density="comfortable"
            hide-details
            inset
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="closeSettingsDialog">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="settings-save-btn"
            color="primary"
            variant="elevated"
            :loading="settingsSaving"
            :disabled="settingsLoading"
            @click="saveSettings"
          >
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { taxConfigStore } from '../utils/taxConfigStore';
import { taxConfigApi } from '../services/taxConfigApi';
import { taxQuestionApi } from '../services/taxQuestionApi';
import { taxCalculateApi } from '../services/taxCalculateApi';
import { taxPlanningSettingsApi } from '../services/taxPlanningSettingsApi';
import TaxYearDialog from '../components/TaxYearDialog.vue';

export default {
  name: 'TaxConfiguration',
  components: { TaxYearDialog },
  data() {
    const currentYear = new Date().getFullYear();
    const currentYearBE = currentYear + 543;

    const years = taxConfigStore.getYears();
    const expandedYears = new Set(years.map((y) => y.taxYear));
    // Version rows start collapsed so their Question/Calculate sub-versions load
    // lazily (on expand) instead of firing 2×N API calls on initial render.
    const expandedVersions = new Set();

    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      headers: [
        { title: 'No.', key: 'no', align: 'center', sortable: false, width: '70px' },
        { title: 'Year / Version', key: 'taxYear', align: 'start', sortable: false, width: '240px' },
        { title: 'Description', key: 'description', align: 'start', sortable: false, width: '260px' },
        { title: 'Effective Start Date', key: 'startDate', align: 'start', sortable: false, width: '170px' },
        { title: 'Effective End Date', key: 'endDate', align: 'start', sortable: false, width: '170px' },
        { title: 'Updated Date', key: 'updatedDate', align: 'start', sortable: false, width: '150px' },
        { title: 'Active', key: 'isActive', align: 'center', sortable: false, width: '110px' },
        { title: 'Action', key: 'actions', align: 'center', sortable: false, width: '280px' },
      ],
      expandedYears,
      expandedVersions,
      // Lazily-loaded sub-versions keyed by `${taxYear}::${versionCode}`.
      questionVersionsByConfig: {},
      calcVersionsByConfig: {},
      subVersionsLoading: {},
      subVersionsError: {},
      taxConfigStoreState: taxConfigStore.state,
      yearOptions: Array.from({ length: 7 }, (_, idx) => currentYearBE - 1 + idx),
      yearDialog: false,
      editYearDialog: false,
      editYearTarget: null,
      versionDialog: false,
      versionFormValid: false,
      versionForm: this.getEmptyVersionForm(currentYear),
      selectedYearForVersion: null,
      deleteDialog: false,
      deleteTarget: null,
      deleteTargetType: '',
      // Lightweight confirm for Question/Calculate sub-version deletes (tree rows).
      subDeleteDialog: false,
      subDeleteTarget: null,
      importDialog: false,
      importFile: null,
      importJsonValid: false,
      importValidating: false,
      importValidationMessage: '',
      importYearForm: {
        beYear: currentYearBE + 1,
        year: currentYear + 1,
        taxYear: `TY${currentYearBE + 1}`,
        description: '',
        effectiveStartDate: `${currentYear + 1}-01-01`,
        effectiveEndDate: `${currentYear + 1}-12-31`,
      },
      importVersionDialog: false,
      importVersionTargetYear: null,
      importVersionFile: null,
      importVersionJsonValid: false,
      importVersionValidating: false,
      importVersionValidationMessage: '',
      importVersionForm: {
        versionCode: 'V1',
        description: '',
        effectiveStartDate: `${currentYear}-01-01`,
        effectiveEndDate: `${currentYear}-12-31`,
      },
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      // Tax Planning global settings dialog (prompts + AI-model config).
      settingsDialog: false,
      settingsSaving: false,
      settingsLoading: false,
      settingsForm: {
        startPromptTh: '',
        resumePromptTh: '',
        yearQuestionPromptTh: '',
        completedPromptTh: '',
        consultCopyTh: '',
        startRefinePrompt: '',
        resumeRefinePrompt: '',
        yearQuestionRefinePrompt: '',
        completedRefinePrompt: '',
        consultRefinePrompt: '',
        enablePromptRefine: false,
        provider: '',
        model: '',
        gender: 'male',
        temperature: 0,
        topP: null,
        frequencyPenalty: null,
        presencePenalty: null,
        maxTokens: null,
        stopSequence: '',
        enableInputMessageReplace: false,
        enableVoicePromptReplace: false,
        enableLanguageReplace: false,
      },
      promptStates: [
        { key: 'start', label: 'Start — ทักทายเริ่มต้น', icon: 'mdi-hand-wave', messageField: 'startPromptTh', refineField: 'startRefinePrompt', messageTestId: 'settings-start-prompt', hint: '' },
        { key: 'resume', label: 'Resume — กลับมาทำต่อ', icon: 'mdi-play-circle-outline', messageField: 'resumePromptTh', refineField: 'resumeRefinePrompt', messageTestId: 'settings-resume-prompt', hint: '' },
        { key: 'year', label: 'Tax Year — ถามปีภาษี', icon: 'mdi-calendar', messageField: 'yearQuestionPromptTh', refineField: 'yearQuestionRefinePrompt', messageTestId: 'settings-year-prompt', hint: '' },
        { key: 'completed', label: 'Completed — เมนูหลังจบ', icon: 'mdi-check-circle-outline', messageField: 'completedPromptTh', refineField: 'completedRefinePrompt', messageTestId: 'settings-completed-prompt', hint: 'ต้องลงท้ายด้วย |quickReply|["เริ่มใหม่","ใช้ข้อมูลเดิม"] มิฉะนั้นปุ่มจะไม่แสดง' },
        { key: 'consult', label: 'Consult — เชิญ/กลับเข้าโหมดปรึกษา', icon: 'mdi-comment-question-outline', messageField: 'consultCopyTh', refineField: 'consultRefinePrompt', messageTestId: 'settings-consult-copy', hint: 'อย่าใส่ปุ่ม |quickReply| เอง — ระบบเติมปุ่ม ["เริ่มใหม่","ถามต่อ"] ให้อัตโนมัติ' },
      ],
      listDDProvider: [],
      listDDModel: [],
      settingsModelOptions: [],
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    yearRows() {
      return taxConfigStore.getYears();
    },
    deleteMessage() {
      return this.deleteTargetType === 'year'
        ? 'Are you sure you want to delete the tax year:'
        : 'Are you sure you want to delete the version:';
    },
    deleteTargetLabel() {
      if (!this.deleteTarget) return '';
      return this.deleteTargetType === 'year'
        ? `${this.deleteTarget.taxYear} (${this.deleteTarget.year})`
        : `${this.deleteTarget.taxYear} / ${this.deleteTarget.versionCode}`;
    },
  },
  watch: {
    'taxConfigStoreState.years': {
      handler(years) {
        if (years && years.length > 0) this._expandAll();
      },
      deep: false,
    },
  },
  mounted() {
    this.reloadFromApi();
  },
  methods: {
    async reloadFromApi() {
      try {
        const years = await taxConfigApi.getYears(true);
        taxConfigStore.loadYearsFromApi(years || []);
        const versions = await taxConfigApi.getVersions(null, true);
        taxConfigStore.loadVersionsFromApi(versions || []);
        this._expandAll();
        this._expandActiveVersions();
      } catch (err) {
        this.showError(err?.message || 'Failed to load tax configuration');
      }
    },
    _expandAll() {
      // Expand every year by default; version rows stay collapsed so their
      // Question/Calculate sub-versions load lazily on expand. Any version rows
      // the user already opened are preserved.
      const years = taxConfigStore.getYears();
      this.expandedYears = new Set(years.map((y) => y.taxYear));
    },
    // Auto-expand each year's active version (single-active-per-year) so its
    // Question/Calculate groups are visible by default, and lazily load them.
    // Version rows the user already opened are preserved.
    _expandActiveVersions() {
      const years = taxConfigStore.getYears();
      const next = new Set(this.expandedVersions);
      years.forEach((y) => {
        const active = (taxConfigStore.getVersions(y.taxYear) || []).find((v) => v.isActive);
        if (!active) return;
        next.add(`${y.taxYear}::${active.versionCode}`);
        this.loadSubVersions(y.taxYear, active.versionCode);
      });
      this.expandedVersions = next;
    },
    getEmptyVersionForm(year) {
      return {
        versionCode: 'V1',
        description: '',
        effectiveStartDate: `${year}-01-01`,
        effectiveEndDate: `${year}-12-31`,
        isActive: false,
      };
    },
    versionsOf(taxYear) {
      return taxConfigStore.getVersions(taxYear);
    },
    isExpanded(taxYear) {
      return this.expandedYears.has(taxYear);
    },
    toggleExpand(taxYear) {
      const next = new Set(this.expandedYears);
      if (next.has(taxYear)) next.delete(taxYear);
      else next.add(taxYear);
      this.expandedYears = next;
    },
    openAddYearDialog() {
      this.yearDialog = true;
    },
    async createYear(formData) {
      const taxYear = String(formData.taxYear || '').trim().toUpperCase();
      if (taxConfigStore.getYear(taxYear)) {
        this.showError(`Tax year ${taxYear} already exists`);
        return;
      }

      const request = {
        beYear: Number(formData.beYear),
        taxYearCode: taxYear,
        description: formData.description || '',
        effectiveStartDate: formData.effectiveStartDate || null,
        effectiveEndDate: formData.effectiveEndDate || null,
        isActive: Boolean(formData.isActive),
      };

      try {
        await taxConfigApi.createYear(request);
        await this.reloadFromApi();
        this.expandedYears = new Set([...this.expandedYears, taxYear]);
        this.yearDialog = false;
        this.showSuccess(`Tax year ${taxYear} created`);
      } catch (err) {
        this.showError(err?.message || `Failed to create tax year ${taxYear}`);
      }
    },
    openAddVersionDialog(yearRow) {
      const nextNo = (taxConfigStore.getVersions(yearRow.taxYear).length || 0) + 1;
      this.selectedYearForVersion = yearRow;
      this.versionForm = {
        ...this.getEmptyVersionForm(yearRow.year),
        versionCode: `V${nextNo}`,
      };
      this.versionDialog = true;
      this.$nextTick(() => this.$refs.versionForm?.resetValidation?.());
    },
    closeVersionDialog() {
      this.versionDialog = false;
      this.selectedYearForVersion = null;
    },
    async createVersion() {
      if (!this.selectedYearForVersion) return;
      const ok = await this.$refs.versionForm?.validate?.();
      if (ok && ok.valid === false) return;

      const code = taxConfigStore.normalizeVersionCode(this.versionForm.versionCode);
      const exists = taxConfigStore.getVersion(this.selectedYearForVersion.taxYear, code);
      if (exists) {
        this.showError(`Version ${code} already exists`);
        return;
      }

      const yearID = this.selectedYearForVersion.yearID;
      if (yearID == null) {
        this.showError('Cannot create version: tax year is not persisted yet');
        return;
      }

      const request = {
        yearID,
        versionCode: code,
        description: this.versionForm.description || '',
        effectiveStartDate: this.versionForm.effectiveStartDate || null,
        effectiveEndDate: this.versionForm.effectiveEndDate || null,
        isActive: Boolean(this.versionForm.isActive),
      };

      const targetTaxYear = this.selectedYearForVersion.taxYear;

      try {
        const dto = await taxConfigApi.createVersion(request);
        if (dto) taxConfigStore.upsertVersionFromApi(dto);
        this.expandedYears = new Set([...this.expandedYears, targetTaxYear]);
        this.closeVersionDialog();
        this.showSuccess(`Version ${code} created`);
      } catch (err) {
        this.showError(err?.message || `Failed to create version ${code}`);
      }
    },
    async onToggleYearActive(taxYear, value) {
      const year = taxConfigStore.getYear(taxYear);
      if (!year || year.yearID == null) {
        taxConfigStore.setYearActive(taxYear, value);
        return;
      }
      try {
        await taxConfigApi.setYearActive(year.yearID, Boolean(value));
        taxConfigStore.setYearActive(taxYear, Boolean(value));
      } catch (err) {
        this.showError(err?.message || 'Failed to update tax year active status');
      }
    },
    async onToggleVersionActive(taxYear, versionCode, value) {
      const version = taxConfigStore.getVersion(taxYear, versionCode);
      if (!version || version.versionID == null) {
        taxConfigStore.setVersionActive(taxYear, versionCode, value);
        return;
      }
      try {
        await taxConfigApi.setVersionActive(version.versionID, Boolean(value));
        // Single-active-per-year is enforced server-side; reload versions to
        // reflect siblings being switched off.
        const versions = await taxConfigApi.getVersions(null, true);
        taxConfigStore.loadVersionsFromApi(versions || []);
      } catch (err) {
        this.showError(err?.message || 'Failed to update active status');
      }
    },
    openEditor(versionRow) {
      this.$router.push(`/TaxConfiguration/${versionRow.taxYear}/${versionRow.versionCode}`);
    },
    openEditYearDialog(row) {
      const beYear = row.year + 543;
      this.editYearTarget = {
        beYear,
        year: row.year,
        taxYear: row.taxYear,
        description: row.description || '',
        effectiveStartDate: row.effectiveStartDate || `${row.year}-01-01`,
        effectiveEndDate: row.effectiveEndDate || `${row.year}-12-31`,
        isActive: row.isActive ?? true,
      };
      this.editYearDialog = true;
    },
    async updateYear(formData) {
      const taxYear = String(formData.taxYear || '').trim().toUpperCase();
      const year = taxConfigStore.getYear(taxYear);
      if (!year || year.yearID == null) {
        this.showError('Cannot update tax year: not persisted yet');
        return;
      }
      const request = {
        description: formData.description || '',
        effectiveStartDate: formData.effectiveStartDate || null,
        effectiveEndDate: formData.effectiveEndDate || null,
        isActive: Boolean(formData.isActive),
      };
      try {
        await taxConfigApi.updateYear(year.yearID, request);
        await this.reloadFromApi();
        this.editYearDialog = false;
        this.editYearTarget = null;
        this.showSuccess(`Tax year ${taxYear} updated`);
      } catch (err) {
        this.showError(err?.message || `Failed to update tax year ${taxYear}`);
      }
    },
    openDeleteYearDialog(row) {
      this.deleteTarget = row;
      this.deleteTargetType = 'year';
      this.deleteDialog = true;
    },
    openDeleteVersionDialog(row) {
      this.deleteTarget = row;
      this.deleteTargetType = 'version';
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.deleteTarget) return;
      if (this.deleteTargetType === 'year') {
        const year = this.deleteTarget;
        if (year.yearID == null) {
          taxConfigStore.deleteYear(year.taxYear);
          this.showSuccess(`Deleted ${year.taxYear}`);
          this.deleteDialog = false;
          this.deleteTarget = null;
          this.deleteTargetType = '';
          return;
        }
        try {
          await taxConfigApi.deleteYear(year.yearID);
          taxConfigStore.deleteYear(year.taxYear);
          this.showSuccess(`Deleted ${year.taxYear}`);
          this.deleteDialog = false;
          this.deleteTarget = null;
          this.deleteTargetType = '';
        } catch (err) {
          this.showError(err?.message || `Failed to delete ${year.taxYear}`);
        }
        return;
      }

      const target = this.deleteTarget;
      const label = `${target.taxYear} / ${target.versionCode}`;

      if (target.versionID == null) {
        taxConfigStore.deleteVersion(target.taxYear, target.versionCode);
        this.showSuccess(`Deleted ${label}`);
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.deleteTargetType = '';
        return;
      }

      try {
        await taxConfigApi.deleteVersion(target.versionID);
        taxConfigStore.deleteVersionByID(target.versionID);
        this.showSuccess(`Deleted ${label}`);
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.deleteTargetType = '';
      } catch (err) {
        this.showError(err?.message || `Failed to delete ${label}`);
      }
    },
    isVersionExpanded(taxYear, versionCode) {
      return this.expandedVersions.has(`${taxYear}::${versionCode}`);
    },
    toggleVersionExpand(taxYear, versionCode) {
      const key = `${taxYear}::${versionCode}`;
      const next = new Set(this.expandedVersions);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Lazily fetch this config version's Question + Calculate sub-versions.
        this.loadSubVersions(taxYear, versionCode);
      }
      this.expandedVersions = next;
    },
    configVersionIdOf(taxYear, versionCode) {
      return taxConfigStore.getVersion(taxYear, versionCode)?.versionID ?? null;
    },
    questionVersionsFor(taxYear, versionCode) {
      return this.questionVersionsByConfig[`${taxYear}::${versionCode}`] || [];
    },
    calcVersionsFor(taxYear, versionCode) {
      return this.calcVersionsByConfig[`${taxYear}::${versionCode}`] || [];
    },
    isSubLoading(taxYear, versionCode) {
      return !!this.subVersionsLoading[`${taxYear}::${versionCode}`];
    },
    subError(taxYear, versionCode) {
      return this.subVersionsError[`${taxYear}::${versionCode}`] || '';
    },
    async loadSubVersions(taxYear, versionCode) {
      const key = `${taxYear}::${versionCode}`;
      // Cache: skip if already loaded successfully or a fetch is in flight.
      if (this.questionVersionsByConfig[key] !== undefined || this.subVersionsLoading[key]) return;

      const configVersionId = this.configVersionIdOf(taxYear, versionCode);
      if (configVersionId == null) {
        // Version not persisted yet — nothing to query; render empty groups.
        this.questionVersionsByConfig[key] = [];
        this.calcVersionsByConfig[key] = [];
        return;
      }

      this.subVersionsLoading[key] = true;
      this.subVersionsError[key] = '';
      try {
        const [qVersions, cVersions] = await Promise.all([
          taxQuestionApi.getVersions(configVersionId, true),
          taxCalculateApi.getVersions(configVersionId, true),
        ]);
        this.questionVersionsByConfig[key] = Array.isArray(qVersions) ? qVersions : [];
        this.calcVersionsByConfig[key] = Array.isArray(cVersions) ? cVersions : [];
      } catch (err) {
        // These endpoints need the DB schema applied; show a subtle inline
        // message instead of crashing, and leave the cache empty so a re-expand
        // retries the fetch.
        this.subVersionsError[key] = err?.message || 'ไม่สามารถโหลดเวอร์ชันได้';
      } finally {
        this.subVersionsLoading[key] = false;
      }
    },
    openQuestionEditor(taxYear, versionCode, questionVersionID) {
      const id = this.configVersionIdOf(taxYear, versionCode);
      if (id == null) return;
      this.$router.push(`/TaxQuestion/${id}/${questionVersionID}`);
    },
    openCalcEditor(taxYear, versionCode, calcVersionID) {
      const id = this.configVersionIdOf(taxYear, versionCode);
      if (id == null) return;
      // Lands on the version's Formula List; each formula drills down into its own Blockly canvas.
      this.$router.push(`/TaxCalculate/${id}/${calcVersionID}`);
    },
    addQuestion(taxYear, versionCode) {
      const id = this.configVersionIdOf(taxYear, versionCode);
      if (id == null) return;
      this.$router.push(`/TaxQuestion/${id}/new`);
    },
    addCalculate(taxYear, versionCode) {
      const id = this.configVersionIdOf(taxYear, versionCode);
      if (id == null) return;
      this.$router.push(`/TaxCalculate/${id}/new`);
    },
    // ── Question / Calculate sub-version row actions (active toggle + delete) ──
    // Single-active-per-config-version is enforced server-side; reload sub-versions
    // so the siblings reflect the switch.
    async toggleQuestionActive(taxYear, versionCode, row, value) {
      try {
        await taxQuestionApi.setVersionActive(row.questionVersionID, Boolean(value));
        await this.reloadSubVersions(taxYear, versionCode);
      } catch (err) {
        this.showError(err?.message || 'Failed to update question version active status');
        await this.reloadSubVersions(taxYear, versionCode);
      }
    },
    async toggleCalcActive(taxYear, versionCode, row, value) {
      try {
        await taxCalculateApi.setVersionActive(row.calcVersionID, Boolean(value));
        await this.reloadSubVersions(taxYear, versionCode);
      } catch (err) {
        this.showError(err?.message || 'Failed to update calc version active status');
        await this.reloadSubVersions(taxYear, versionCode);
      }
    },
    // Re-fetch a single config version's Question + Calculate sub-versions,
    // bypassing the load cache (used after an active toggle / delete).
    async reloadSubVersions(taxYear, versionCode) {
      const key = `${taxYear}::${versionCode}`;
      const configVersionId = this.configVersionIdOf(taxYear, versionCode);
      if (configVersionId == null) {
        this.questionVersionsByConfig[key] = [];
        this.calcVersionsByConfig[key] = [];
        return;
      }
      this.subVersionsError[key] = '';
      try {
        const [qVersions, cVersions] = await Promise.all([
          taxQuestionApi.getVersions(configVersionId, true),
          taxCalculateApi.getVersions(configVersionId, true),
        ]);
        this.questionVersionsByConfig[key] = Array.isArray(qVersions) ? qVersions : [];
        this.calcVersionsByConfig[key] = Array.isArray(cVersions) ? cVersions : [];
      } catch (err) {
        this.subVersionsError[key] = err?.message || 'ไม่สามารถโหลดเวอร์ชันได้';
      }
    },
    deleteQuestionVersion(taxYear, versionCode, row) {
      this.subDeleteTarget = { kind: 'question', taxYear, versionCode, row };
      this.subDeleteDialog = true;
    },
    deleteCalcVersion(taxYear, versionCode, row) {
      this.subDeleteTarget = { kind: 'calc', taxYear, versionCode, row };
      this.subDeleteDialog = true;
    },
    async confirmSubDelete() {
      const target = this.subDeleteTarget;
      if (!target) return;
      const { kind, taxYear, versionCode, row } = target;
      try {
        if (kind === 'question') {
          await taxQuestionApi.deleteVersion(row.questionVersionID);
        } else {
          await taxCalculateApi.deleteVersion(row.calcVersionID);
        }
        this.subDeleteDialog = false;
        this.subDeleteTarget = null;
        await this.reloadSubVersions(taxYear, versionCode);
        this.showSuccess(`Deleted ${row.versionCode}`);
      } catch (err) {
        this.showError(err?.message || `Failed to delete ${row.versionCode}`);
      }
    },
    async onExportJson(versionRow) {
      try {
        // Server produces the portable, business-key-only bundle (config version +
        // rules + all calc versions + all question versions) — not the in-memory store.
        const bundle = await taxConfigApi.exportBundle(versionRow.versionID);
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tax-config-${versionRow.taxYear}-${versionRow.versionCode}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.showError(err?.message || 'Export failed');
      }
    },
    openImportDialog() {
      const nextBE = new Date().getFullYear() + 543 + 1;
      const nextCE = nextBE - 543;
      this.importYearForm = {
        beYear: nextBE,
        year: nextCE,
        taxYear: `TY${nextBE}`,
        description: '',
        effectiveStartDate: `${nextCE}-01-01`,
        effectiveEndDate: `${nextCE}-12-31`,
      };
      this.importFile = null;
      this.importJsonValid = false;
      this.importValidating = false;
      this.importValidationMessage = '';
      this.importDialog = true;
    },
    closeImportDialog() {
      this.importDialog = false;
      this.importFile = null;
      this.importJsonValid = false;
      this.importValidating = false;
      this.importValidationMessage = '';
    },
    onImportYearChange(beYear) {
      const ceYear = Number(beYear) - 543;
      this.importYearForm.beYear = Number(beYear);
      this.importYearForm.year = ceYear;
      this.importYearForm.taxYear = `TY${beYear}`;
      this.importYearForm.effectiveStartDate = `${ceYear}-01-01`;
      this.importYearForm.effectiveEndDate = `${ceYear}-12-31`;
    },
    onImportFileChange(file) {
      this.importFile = file || null;
      this.importJsonValid = false;
      this.importValidationMessage = '';
    },
    async onValidateImportJson() {
      if (!this.importFile) return;
      this.importValidating = true;
      this.importValidationMessage = '';
      try {
        const raw = await this.importFile.text();
        const result = this.validateTaxConfigJson(JSON.parse(raw));
        this.importJsonValid = result.isValid;
        this.importValidationMessage = result.message;
      } catch {
        this.importJsonValid = false;
        this.importValidationMessage = 'Invalid JSON: cannot parse file content.';
      } finally {
        this.importValidating = false;
      }
    },
    async onImportConfig() {
      if (!this.importFile || !this.importJsonValid) return;
      try {
        const parsed = JSON.parse(await this.importFile.text());
        const req = {
          bundle: parsed,
          target: {
            mode: 'newYear',
            beYear: Number(this.importYearForm.beYear),
            taxYearCode: String(this.importYearForm.taxYear || '').trim().toUpperCase(),
            description: this.importYearForm.description?.trim() || '',
            effectiveStartDate: this.importYearForm.effectiveStartDate,
            effectiveEndDate: this.importYearForm.effectiveEndDate,
            configVersionIsActive: false,
          },
        };
        const res = await taxConfigApi.importBundle(req);
        // Re-fetch years+versions from the API so the imported bundle is persisted
        // and visible (rebuilds the store from the DB — no local mutations).
        await this.reloadFromApi();
        this._expandImportedYear(res.yearId, req.target.taxYearCode);
        this.closeImportDialog();
        this.showSuccess(this._importToast(res));
      } catch (err) {
        this.importJsonValid = false;
        this.importValidationMessage = err?.message || 'Failed to import bundle.';
        this.showError(err?.message || 'Failed to import bundle.');
      }
    },
    openImportVersionDialog(yearRow) {
      const nextNo = (taxConfigStore.getVersions(yearRow.taxYear).length || 0) + 1;
      this.importVersionTargetYear = yearRow;
      this.importVersionForm = {
        versionCode: `V${nextNo}`,
        description: '',
        effectiveStartDate: `${yearRow.year}-01-01`,
        effectiveEndDate: `${yearRow.year}-12-31`,
      };
      this.importVersionFile = null;
      this.importVersionJsonValid = false;
      this.importVersionValidating = false;
      this.importVersionValidationMessage = '';
      this.importVersionDialog = true;
    },
    closeImportVersionDialog() {
      this.importVersionDialog = false;
      this.importVersionTargetYear = null;
      this.importVersionFile = null;
      this.importVersionJsonValid = false;
      this.importVersionValidating = false;
      this.importVersionValidationMessage = '';
    },
    onImportVersionFileChange(file) {
      this.importVersionFile = file || null;
      this.importVersionJsonValid = false;
      this.importVersionValidationMessage = '';
    },
    async onValidateImportVersionJson() {
      if (!this.importVersionFile) return;
      this.importVersionValidating = true;
      this.importVersionValidationMessage = '';
      try {
        const raw = await this.importVersionFile.text();
        const result = this.validateTaxConfigJson(JSON.parse(raw));
        this.importVersionJsonValid = result.isValid;
        this.importVersionValidationMessage = result.message;
      } catch {
        this.importVersionJsonValid = false;
        this.importVersionValidationMessage = 'Invalid JSON: cannot parse file content.';
      } finally {
        this.importVersionValidating = false;
      }
    },
    async onImportVersionConfig() {
      if (!this.importVersionTargetYear || !this.importVersionFile || !this.importVersionJsonValid) return;
      try {
        const parsed = JSON.parse(await this.importVersionFile.text());
        const req = {
          bundle: parsed,
          target: {
            mode: 'existingYear',
            yearId: this.importVersionTargetYear.yearID,
            configVersionDescription: this.importVersionForm.description?.trim() || '',
          },
        };
        const res = await taxConfigApi.importBundle(req);
        // Re-fetch from the API so the grafted version is persisted + visible.
        await this.reloadFromApi();
        this._expandImportedYear(res.yearId, this.importVersionTargetYear.taxYear);
        this.closeImportVersionDialog();
        this.showSuccess(this._importToast(res));
      } catch (err) {
        this.importVersionJsonValid = false;
        this.importVersionValidationMessage = err?.message || 'Failed to import bundle.';
        this.showError(err?.message || 'Failed to import bundle.');
      }
    },
    // Expand the year that received an imported bundle. Prefer the freshly-reloaded
    // store row (matched by DB yearId); fall back to the code we targeted.
    _expandImportedYear(yearId, fallbackCode) {
      const row = (taxConfigStore.getYears() || []).find((y) => y.yearID === yearId);
      const code = row ? row.taxYear : fallbackCode;
      if (code) this.expandedYears = new Set([...this.expandedYears, code]);
    },
    // Success toast for an import result: final version code + auto-adjust + warning notes.
    _importToast(res) {
      let msg = `Imported ${res.versionCode}`;
      if (res.versionCodeAdjusted) msg += ' (version code auto-adjusted)';
      const n = res.warnings?.length || 0;
      if (n) msg += ` (${n} warning${n > 1 ? 's' : ''})`;
      return msg;
    },
    // Client-side sanity check of the bundle envelope. The server does the
    // authoritative validation on import; this just gates the Import button and
    // reports the payload shape.
    validateTaxConfigJson(payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return { isValid: false, message: 'Invalid bundle: root must be an object.' };
      }
      const looksLikeBundle =
        payload.kind === 'lhb.taxconfig.bundle' ||
        (payload.configVersion && typeof payload.configVersion === 'object' && !Array.isArray(payload.configVersion)) ||
        Array.isArray(payload.rules);
      if (!looksLikeBundle) {
        return {
          isValid: false,
          message: 'Invalid bundle: expected a tax config bundle (missing kind/configVersion/rules).',
        };
      }
      if (!Array.isArray(payload.rules)) {
        return { isValid: false, message: 'Invalid bundle: `rules` must be an array.' };
      }
      const ruleCount = payload.rules.length;
      const calcCount = Array.isArray(payload.calcVersions) ? payload.calcVersions.length : 0;
      const questionCount = Array.isArray(payload.questionVersions) ? payload.questionVersions.length : 0;
      return {
        isValid: true,
        message: `Valid bundle: ${ruleCount} rule(s), ${calcCount} calc version(s), ${questionCount} question version(s).`,
      };
    },
    // ---- Tax Planning settings (prompts + AI model) ----
    loadModelOptions() {
      // Mirror Flow.vue GetAllDDModelManagement: fills provider + model master lists.
      return new Promise((resolve) => {
        const self = this;
        self.$call.GetAllDDModelManagement(
          0,
          (dataResponse) => {
            if (dataResponse && dataResponse.status === 0 && dataResponse.data) {
              self.listDDProvider = dataResponse.data.provider || [];
              self.listDDModel = dataResponse.data.modelData || [];
            } else {
              self.listDDProvider = [];
              self.listDDModel = [];
            }
            resolve();
          },
          () => {
            self.listDDProvider = [];
            self.listDDModel = [];
            resolve();
          }
        );
      });
    },
    onSettingsProviderChange(provider) {
      // Filter models by the selected provider (check both providerName/provider).
      this.settingsModelOptions = this.listDDModel.filter(
        (m) => m.providerName === provider || m.provider === provider
      );
      // Clear model if it no longer belongs to the new provider's list.
      if (
        this.settingsForm.model &&
        !this.settingsModelOptions.some((m) => m.modelName === this.settingsForm.model)
      ) {
        this.settingsForm.model = '';
      }
    },
    async openSettingsDialog() {
      // Open the dialog immediately with a loading state so the button gives instant feedback,
      // instead of a dead pause while model options + settings are fetched (two awaited calls).
      this.settingsLoading = true;
      this.settingsDialog = true;
      try {
        await this.loadModelOptions();
        const dto = await taxPlanningSettingsApi.getSettings();
        if (dto) {
          this.settingsForm = {
            startPromptTh: dto.startPromptTh || '',
            resumePromptTh: dto.resumePromptTh || '',
            yearQuestionPromptTh: dto.yearQuestionPromptTh || '',
            completedPromptTh: dto.completedPromptTh || '',
            consultCopyTh: dto.consultCopyTh || '',
            startRefinePrompt: dto.startRefinePrompt || '',
            resumeRefinePrompt: dto.resumeRefinePrompt || '',
            yearQuestionRefinePrompt: dto.yearQuestionRefinePrompt || '',
            completedRefinePrompt: dto.completedRefinePrompt || '',
            consultRefinePrompt: dto.consultRefinePrompt || '',
            enablePromptRefine: !!dto.enablePromptRefine,
            provider: dto.provider || '',
            model: dto.model || '',
            gender: dto.gender || 'male',
            temperature: dto.temperature ?? 0,
            topP: dto.topP ?? null,
            frequencyPenalty: dto.frequencyPenalty ?? null,
            presencePenalty: dto.presencePenalty ?? null,
            maxTokens: dto.maxTokens ?? null,
            stopSequence: dto.stopSequence || '',
            enableInputMessageReplace: !!dto.enableInputMessageReplace,
            enableVoicePromptReplace: !!dto.enableVoicePromptReplace,
            enableLanguageReplace: !!dto.enableLanguageReplace,
          };
        }
        if (this.settingsForm.provider) {
          this.onSettingsProviderChange(this.settingsForm.provider);
        } else {
          this.settingsModelOptions = [];
        }
      } catch (e) {
        this.settingsDialog = false;
        this.showError(e && e.message ? e.message : 'Failed to load settings');
      } finally {
        this.settingsLoading = false;
      }
    },
    async saveSettings() {
      this.settingsSaving = true;
      try {
        await taxPlanningSettingsApi.saveSettings({ ...this.settingsForm });
        this.showSuccess('Settings saved successfully');
        this.settingsDialog = false;
      } catch (e) {
        this.showError(e && e.message ? e.message : 'Failed to save settings');
      } finally {
        this.settingsSaving = false;
      }
    },
    closeSettingsDialog() {
      this.settingsDialog = false;
    },
    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* House-style form grouping (mirrors SystemConfiguration.vue et al.): plain section headings,
   no per-section icons, so the settings dialog reads as part of the console — not a one-off. */
.form-section {
  margin-bottom: 24px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}
.cfg-tree-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  font: inherit;
  text-align: left;
  color: inherit;
}

.cfg-tree-chevron {
  transition: transform 0.2s ease;
}

.cfg-tree-chevron.is-open {
  transform: rotate(90deg);
}

.cfg-tree-title-block {
  display: inline-flex;
  flex-direction: column;
}

.cfg-tree-title {
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.cfg-tree-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
}

.cfg-version-child-row td {
  background: rgba(0, 0, 0, 0.02);
}

.cfg-version-child-cell {
  position: relative;
  padding-left: 36px !important;
}

.cfg-version-branch {
  position: absolute;
  left: 16px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #c4c4c4;
}

.cfg-version-branch::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background: #c4c4c4;
}

/* ── In-page Question / Calculate sub-version tree ──────────────────────── */
.cfg-subtree-status {
  padding-left: 72px !important;
  font-size: 0.82rem;
  color: #6b7280;
}

.cfg-subtree-status.is-error {
  color: rgb(var(--v-theme-error));
}

/* Group header rows (Tax Question / Tax Calculate) */
.cfg-subgroup-cell {
  position: relative;
  padding-left: 72px !important;
}

.cfg-subgroup-question .cfg-subgroup-cell {
  background: rgba(var(--v-theme-primary), 0.06);
}

.cfg-subgroup-calculate .cfg-subgroup-cell {
  background: rgba(var(--v-theme-info), 0.06);
}

/* Strong divider between the Question group and the Calculate group */
.cfg-subgroup-calculate td {
  border-top: 2px solid rgba(0, 0, 0, 0.08);
}

.cfg-subgroup-branch {
  position: absolute;
  left: 52px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #c4c4c4;
}

.cfg-subgroup-branch::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background: #c4c4c4;
}

.cfg-subgroup-header {
  display: inline-flex;
  align-items: center;
}

.cfg-subgroup-title {
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cfg-subgroup-question .cfg-subgroup-title {
  color: rgb(var(--v-theme-primary));
}

.cfg-subgroup-calculate .cfg-subgroup-title {
  color: rgb(var(--v-theme-info));
}

.cfg-subgroup-count {
  margin-left: 6px;
  font-size: 0.75rem;
  color: #6b7280;
}

.cfg-subgroup-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
}

/* Individual sub-version rows */
.cfg-subversion-row td {
  background: rgba(0, 0, 0, 0.015);
}

.cfg-subversion-cell {
  position: relative;
  padding-left: 96px !important;
}

.cfg-subversion-branch {
  position: absolute;
  left: 76px;
  top: 50%;
  width: 14px;
  height: 1px;
  background: #c4c4c4;
}

.cfg-subversion-branch::before {
  content: '';
  position: absolute;
  left: 0;
  top: -16px;
  width: 1px;
  height: 32px;
  background: #c4c4c4;
}

.cfg-subversion-code {
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.cfg-subversion-empty-cell {
  padding-left: 96px !important;
  color: #9e9e9e;
  font-size: 0.8rem;
  font-style: italic;
}

.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 180px;
}

.action-btn {
  transition: transform 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.action-btn:deep(.v-btn__underlay) {
  opacity: 0.22;
}

.action-btn:hover:deep(.v-btn__underlay) {
  opacity: 0.32;
}

:deep(.my-data-table .v-table__wrapper) {
  max-height: 620px !important;
  overflow: auto !important;
}

:deep(.my-data-table table) {
  table-layout: fixed;
  min-width: 1400px;
  width: 100%;
}

:deep(.my-data-table thead th),
:deep(.my-data-table tbody td) {
  vertical-align: middle;
}

:deep(.my-data-table tbody td) {
  height: 56px;
  padding: 0 16px;
}
</style>
